import { AdyenCheckout } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import axios from 'axios';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../../Utils/logger';
import PaymentStatus from './PaymentStatus';
import { useBookingTimer } from '../../Context/Timer/useBookingTimer';
import { useCustomer } from '../../Context/Customer/CustomerContext';
import { useBookingSession } from '../../Context/BookingSession/BookingSessionContext';
import { useVoucherContext } from '../../Context/Voucher/VoucherContext';
import "./payment.css";

import ExpressCheckout from "../payment/ExpressCheckout";
import PaymentAccordion from "../payment/PaymentAccordion";
import PaymentMethodRenderer from "../payment/PaymentMethodRenderer";
import paymentStyles from "../payment/payment.module.css";

import {
  PAYMENT_METHODS,
  PAYMENT_IMAGES,
  buildPaymentMethodRows,
  createPaymentComponent,
  createApplePayComponent,
  createGooglePayComponent,
} from "../payment/paymentConfig";
import { navigateToBookedAppointment } from "../../Utils/navigateToBookedAppointment";
import ErrorBoundary from "../ErrorBoundary";
import {
  getPaymentLogDetails,
  getVoucherPaymentError,
  getVoucherPaymentFailureMessage,
  getVoucherPaymentFeedback,
  isVoucherPaymentCancellation,
  isVoucherPaymentServiceError,
} from "../../Utils/voucherPaymentError";
import { createAdditionalDetailsHandler } from "../payment/additionalDetailsHandler";

import usePaymentSecurityPolicy from "../../Utils/Hooks/usePaymentSecurityPolicy";

export { PAYMENT_METHODS, PAYMENT_IMAGES };

// Client-side retry lockouts removed for now — Adyen's own session limits are
// the gate (error 14_0422 "session exhausted" is handled as session expiry).

/** Adyen error 14_0422: session exhausted after too many failures or session expired.
 *  The Adyen SDK wraps HTTP errors differently across versions — check every known field. */
function isAdyenSessionExpiredError(err) {
  if (!err || typeof err !== "object") return false;
  const code =
    err.errorCode ??
    err.response?.data?.errorCode ??
    err.error?.errorCode ??
    err.data?.errorCode;
  if (code === "14_0422") return true;
  // Validation responses can also use 422; only a session-specific code or
  // message should trigger the automatic return to billing.
  // SDK message fallback
  const msg = typeof err.message === "string" ? err.message : "";
  return msg.includes("session identifier") || msg.includes("14_0422");
}

/** Legacy lockout keys — kept only to clear tokens written by older builds. */
const LOCK_KEYS = [...Object.values(PAYMENT_METHODS), "card_new", "card_saved"];

/** Lockouts disabled: children still take lock props, so feed them empty state. */
const NO_LOCKS = {};

function unmountComponentEntry(entry) {
  try {
    entry?.remove?.();
  } catch {
    /* noop */
  }
}

const normStr = (v) => (v == null || v === "" ? "" : String(v).trim().toLowerCase());

/** Wallet dismissed, shopper canceled checkout, or Adyen `Cancelled` — not a hard decline. */
function isUserCancellationLike(obj) {
  if (obj == null || typeof obj !== "object") return false;
  const rc = normStr(obj.resultCode);
  const ec = normStr(obj.errorCode);
  const name = normStr(obj.name);
  const refusal = normStr(obj.refusalReason || obj.sessionResult?.refusalReason);
  const msg = normStr(obj.message);

  if (["cancelled", "canceled", "cancel"].includes(rc)) return true;
  if (["cancel", "canceled", "cancelled"].includes(ec)) return true;
  if (name === "cancel") return true;
  if (refusal.includes("cancel")) return true;
  if (msg && msg.includes("cancel") && !msg.includes("subscription")) return true;

  const sr = obj.sessionResult;
  if (sr && typeof sr === "object") {
    const src = normStr(sr.refusalReason || sr.resultCode);
    if (src.includes("cancel") || ["cancelled", "canceled"].includes(src)) return true;
  }
  return false;
}

/**
 * Apple Pay / Google Pay often report failure with no `resultCode` when the shopper closes the sheet
 * (leads to "Payment failed with code undefined" if treated as an error).
 */
function isAmbiguousShopperAbort(data) {
  if (!data || typeof data !== "object") return false;
  const rc = data.resultCode;
  const ec = data.errorCode;
  const hasRefusal = Boolean(data.refusalReason || data.sessionResult?.refusalReason);
  return (
    (rc == null || rc === "") &&
    (ec == null || ec === "") &&
    !hasRefusal
  );
}

function isComponentValidationOnlyError(err) {
  if (!err || typeof err !== "object") return false;

  return !(
    err.resultCode ||
    err.sessionResult ||
    err.refusalReason ||
    err.pspReference ||
    err.paymentData
  );
}

const PaymentMethod = ({ amount, currency, voucherOrderData }) => {
  const { setIsTimerActive, clearTimerAfterPayment } = useBookingTimer();
  const { userID } = useCustomer();
  const { uuid, setUuid } = useBookingSession();
  const navigate = useNavigate();

  // For voucher payments, use order ID instead of booking uuid
  const isVoucherPayment = Boolean(voucherOrderData);
  const paymentUUID = isVoucherPayment ? voucherOrderData?.order?.id : uuid;
  const effectiveUserID = isVoucherPayment ? voucherOrderData?.order?.user_id : userID;

  const [complete, setComplete] = useState(false);
  const [reference, setReference] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [authenticationBlocked, setAuthenticationBlocked] = useState(false);
  const [activeMethod, setActiveMethod] = useState(null);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [mountingPm, setMountingPm] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const error = paymentError?.message || null;
  // Local errors stay inline. API callbacks explicitly opt into modal feedback.
  const setError = useCallback((message) => {
    setPaymentError(message ? { message, source: "local" } : null);
  }, []);
  const [pmResponse, setPmResponse] = useState(null);
  const [cardStored, setCardStored] = useState(false);
  const [selectedStoredCard, setSelectedStoredCard] = useState(null);
  const { setOrderData: setVoucherOrderData } = useVoucherContext();
  const encryptedCvcRef = useRef("");
  const [cardOption, setCardOption] = useState("new");
  const [supportsApplePay, setSupportsApplePay] = useState(false);
  const [supportsGooglePay, setSupportsGooglePay] = useState(false);
  /** Bumps when user cancels a payment so express + accordion Adyen mounts refresh cleanly. */
  const [pmRemountTick, setPmRemountTick] = useState(0);

  const checkoutRef = useRef(null);
  const checkoutErrorHandlerRef = useRef(null);
  const componentsRef = useRef({});
  const scrollAnchorRef = useRef(null);

  const applePayContainerRef = useRef(null);
  const googlePayContainerRef = useRef(null);
  const paymentMountRef = useRef(null);
  const sessionInitiatedRef = useRef(false);
  const previousUuidRef = useRef(null);

  /** Latest values for Adyen session callbacks (avoid stale closures). */
  const ctxRef = useRef({
    activeMethod: null,
    cardOption: "new",
    selectedStoredCard: null,
  });

  useEffect(() => {
    ctxRef.current = { activeMethod, cardOption, selectedStoredCard };
  }, [activeMethod, cardOption, selectedStoredCard]);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const clientKey = process.env.REACT_APP_ADYEN_CLIENT_KEY;

  const paymentRows = useMemo(() => buildPaymentMethodRows(pmResponse), [pmResponse]);

  usePaymentSecurityPolicy(
    isVoucherPayment && checkoutReady && activeMethod === PAYMENT_METHODS.CARD && !complete,
    (directive) => {
      logger.error("Bank verification blocked by security policy", { directive });
      setAuthenticationBlocked(true);
      setSessionExpired(false);
      setError("We couldn’t open your bank’s verification screen. Please choose another payment method or contact our team.");
    }
  );

  const resolveAttemptKeyForCallbacks = useCallback(() => {
    const { activeMethod: am, cardOption: co } = ctxRef.current;
    if (!am) {
      if (componentsRef.current[PAYMENT_METHODS.APPLEPAY]) return PAYMENT_METHODS.APPLEPAY;
      if (componentsRef.current[PAYMENT_METHODS.GOOGLEPAY]) return PAYMENT_METHODS.GOOGLEPAY;
      return null;
    }
    if (am === PAYMENT_METHODS.CARD) return co === "new" ? "card_new" : "card_saved";
    return am;
  }, []);

  const softResetAfterUserCancel = useCallback(() => {
    setAuthenticationBlocked(false);
    setSessionExpired(false);
    setLoading(false);
    setError(null);
    // Dismissing a wallet is not a failed attempt and does not erase real declines.
    setPmRemountTick((t) => t + 1);
  }, [setError]);

  // No attempt counting here any more — Adyen decides when the session is
  // exhausted; this only maps result codes to shopper-facing copy.
  const handleFailedAttempt = useCallback(
    (sessionResult, code) => {
      const errorMessages = {
        Refused: "Payment declined. Please check your card details or try another card.",
        INVALID_CARD: "Invalid card details. Please verify and try again.",
        NOT_SUPPORTED: "This payment method is not supported for this transaction.",
        ERROR: "An error occurred during payment. Please try again later.",
        CANCEL: "Payment was cancelled. Please try again or choose another method.",
        NOT_3D_SECURE: "Payment failed due to 3D Secure authentication issue.",
      };

      return isVoucherPayment
        ? getVoucherPaymentFailureMessage(code)
        : errorMessages[code] || `Payment failed with code ${code}. Please try again or choose another method.`;
    },
    [isVoucherPayment]
  );

  const unmountRegularComponents = useCallback(() => {
    Object.entries(componentsRef.current).forEach(([key, component]) => {
      if (key !== PAYMENT_METHODS.APPLEPAY && key !== PAYMENT_METHODS.GOOGLEPAY) {
        unmountComponentEntry(component);
        delete componentsRef.current[key];
      }
    });
    if (paymentMountRef.current) paymentMountRef.current.innerHTML = "";
  }, []);

  useEffect(() => {
    // Clear lockout tokens written by older builds so nothing stays blocked.
    LOCK_KEYS.forEach((key) => sessionStorage.removeItem(`failedAttemptsToken_${key}`));

    // Only initiate if paymentUUID exists and has changed from the previous session
    if (paymentUUID && previousUuidRef.current !== paymentUUID) {
      previousUuidRef.current = paymentUUID;
      sessionInitiatedRef.current = true;
      initiateCheckoutSession();
    }

    return () => {
      Object.values(componentsRef.current).forEach(unmountComponentEntry);
      componentsRef.current = {};
      setSelectedStoredCard(null);
      encryptedCvcRef.current = "";
      setActiveMethod(null);
      setSelectedRowKey(null);
      setCheckoutReady(false);
    };
    // Session bootstrap intentionally excludes unstable initiateCheckoutSession identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- booking/voucher bootstrap only
  }, [paymentUUID, effectiveUserID]);

  const handleVoucherPaymentEvent = useCallback((event, stage = "payment") => {
    if (isVoucherPaymentCancellation(event)) {
      softResetAfterUserCancel();
      return;
    }
    const feedback = getVoucherPaymentFeedback(event, stage);
    if (feedback) {
      const details = getVoucherPaymentError(event);
      if (feedback.allowRetry && stage === "payment") {
        handleFailedAttempt(details.sessionResult, details.resultCode || details.errorCode, ctxRef.current.activeMethod);
      }
      setAuthenticationBlocked(false);
      setSessionExpired(Boolean(feedback.sessionExpired));
      setPaymentError(feedback);
      setLoading(false);
      return;
    }

    // An empty onPaymentFailed can follow actions.reject(). Do not replace an
    // API failure already reported by onAdditionalDetails with a cancellation.
    const details = getVoucherPaymentError(event);
    const networkError = details.name === "NETWORK_ERROR" || ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(details.code);
    if (["PENDING", "RECEIVED"].includes(String(details.resultCode || "").toUpperCase())) {
      setPaymentError({ source: "local", title: "Your payment is being processed", allowRetry: false,
        message: "We’re waiting for confirmation from your payment provider. Please don’t pay again. Contact Natureland with your order reference if you need help checking your payment." });
    } else if (stage === "setup") {
      setPaymentError({ source: "local", title: "Payment options are unavailable",
        message: "Please check your connection and reload your payment options. Your voucher selection and billing details are saved.",
        retryLabel: "Reload payment options" });
    } else if (networkError || stage === "confirmation") {
      setPaymentError({ source: "local", title: "We couldn’t confirm your payment", allowRetry: false,
        message: "Check your bank or wallet activity before making another payment. If you see a charge, contact Natureland with your order reference so we can check it for you." });
    } else if (["ERROR", "IMPLEMENTATION_ERROR", "SDK_ERROR", "SCRIPT_ERROR"].includes(details.name)) {
      setError("This payment option isn’t available right now. Please choose another payment method.");
    }
  }, [handleFailedAttempt, setError, softResetAfterUserCancel]);

  const handleCheckoutError = useCallback((err) => {
    if (isVoucherPayment) {
      handleVoucherPaymentEvent(err);
      return;
    }
    logger.error("[Adyen onError]", getPaymentLogDetails(err));
    if (isVoucherPayment) err = getVoucherPaymentError(err);
    const expressFallback =
      componentsRef.current[PAYMENT_METHODS.APPLEPAY] != null
        ? PAYMENT_METHODS.APPLEPAY
        : PAYMENT_METHODS.GOOGLEPAY;
    const errCode = err.errorCode || err.name;

    if (
      errCode === "CANCEL" ||
      errCode === "CANCELED" ||
      isUserCancellationLike(err) ||
      isUserCancellationLike({ ...err, errorCode: errCode })
    ) {
      softResetAfterUserCancel();
      return;
    }

    if (isAdyenSessionExpiredError(isVoucherPayment ? getVoucherPaymentError(err) : err)) {
      setSessionExpired(true);
      setError("Too many failed payment attempts. Your session has expired. Please return to billing details to try again.");
      return;
    }

    const methodForErr = ctxRef.current.activeMethod || expressFallback;
    if (
      methodForErr === PAYMENT_METHODS.CARD &&
      isComponentValidationOnlyError(err) &&
      (!isVoucherPayment || !isVoucherPaymentServiceError(err))
    ) {
      encryptedCvcRef.current = "";
      return;
    }

    const errorMessage = handleFailedAttempt(
      err.sessionResult,
      err.resultCode || err.errorCode,
      methodForErr
    );
    setError(errorMessage);
    if (errorMessage.toLowerCase().includes("too many")) setSessionExpired(true);
  }, [handleFailedAttempt, handleVoucherPaymentEvent, isVoucherPayment, setError, softResetAfterUserCancel]);
  checkoutErrorHandlerRef.current = handleCheckoutError;

  const initiateCheckoutSession = async () => {
    if (!baseUrl || !clientKey) {
      setPaymentError({ source: "local", allowRetry: false, message: "Payment options are unavailable right now. Please return to your billing details and try again later." });
      setLoading(false);
      setCheckoutReady(false);
      setSupportsApplePay(false);
      setSupportsGooglePay(false);
      return;
    }

    if (!effectiveUserID) {
      setPaymentError({ source: "local", allowRetry: false, message: "We couldn’t load the details for this order. Please return to your billing details and save them again." });
      setLoading(false);
      setCheckoutReady(false);
      setSupportsApplePay(false);
      setSupportsGooglePay(false);
      return;
    }

    let adjustedShopperReference = effectiveUserID.toString();
    if (adjustedShopperReference.length < 3) {
      adjustedShopperReference = adjustedShopperReference.padStart(3, "0");
    }

    setLoading(true);
    setCheckoutReady(false);
    setSessionExpired(false);
    setAuthenticationBlocked(false);
    setError(null);
    setComplete(false);
    setCardStored(false);
    setSupportsApplePay(false);
    setSupportsGooglePay(false);

    try {
      let payload;

      if (isVoucherPayment) {
        // Voucher payment payload
        payload = {
          type: "voucher",
          uuid: paymentUUID,
          amount: {
            value: amount * 100,
            currency,
          },
          panel: "customer",
        };
      } else {
        // Booking payment payload
        payload = {
          customerID: adjustedShopperReference,
          uuid: paymentUUID,
          amount: { value: amount * 100, currency },
          shopperReference: adjustedShopperReference,
          panel: "customer",
        };
      }

      const { activeMethod: am, selectedStoredCard: sc } = ctxRef.current;
      if (am === PAYMENT_METHODS.CARD && !sc) {
        payload.recurringProcessingModel = "CardOnFile";
      }

      logger.info('Payment session initiated', { uuid: payload.uuid, panel: payload.panel });

      // No React Query retry here — payment initiate must not auto-retry (duplicate charges).
      const { data } = await axios.post(`${baseUrl}/payment/initiate/oneclick`, payload, {
        timeout: 30000,
      });
      setReference(data.reference);

      const checkout = await AdyenCheckout({
        environment: "test",
        clientKey,
        session: { id: data.id, sessionData: data.sessionData },
        amount,
        locale: "en-US",
        countryCode: "SG",
        onPaymentCompleted: async (result) => {
          if (result.resultCode === "Authorised") {
            setComplete(true);
            setIsTimerActive(false);
            clearTimerAfterPayment();
            if (!isVoucherPayment) setUuid(null);

            const { activeMethod: amCb, selectedStoredCard: scCb } = ctxRef.current;
            if (
              amCb === PAYMENT_METHODS.CARD &&
              !scCb &&
              result.additionalData?.storedPaymentMethodId
            ) {
              setCardStored(true);
            }

            if (isVoucherPayment) {
              const orderId = voucherOrderData?.order?.id || voucherOrderData?.id;
              navigate(`/voucher/${orderId}`);
            } else {
              navigateToBookedAppointment(navigate, uuid);
            }
          } else {
            if (isVoucherPayment) {
              handleVoucherPaymentEvent(result);
              return;
            }
            const expressFallback =
              componentsRef.current[PAYMENT_METHODS.APPLEPAY] != null
                ? PAYMENT_METHODS.APPLEPAY
                : PAYMENT_METHODS.GOOGLEPAY;
            if (isUserCancellationLike(result)) {
              softResetAfterUserCancel();
            } else {
              const errMsg = handleFailedAttempt(
                result.sessionResult,
                result.resultCode,
                ctxRef.current.activeMethod || expressFallback
              );
              setError(errMsg);
              if (errMsg.toLowerCase().includes("too many")) setSessionExpired(true);
            }
          }
        },
        onAdditionalDetails: createAdditionalDetailsHandler({
          baseUrl,
          amount,
          currency,
          onError: (err) => {
            logger.error("Error processing additional details:", getPaymentLogDetails(err));
            if (isVoucherPayment) {
              handleVoucherPaymentEvent(err, "confirmation");
              return;
            }
            setError(isVoucherPayment ? getVoucherPaymentFailureMessage() : "Failed to process payment details. Please try again.");
          },
        }),
        onPaymentFailed: (data, component) => {
          if (isVoucherPayment) {
            handleVoucherPaymentEvent(data);
            return;
          }
          // actions.reject() in onAdditionalDetails can land here with no payload.
          data = isVoucherPayment ? getVoucherPaymentError(data) : data || {};
          const expressFallback =
            componentsRef.current[PAYMENT_METHODS.APPLEPAY] != null
              ? PAYMENT_METHODS.APPLEPAY
              : PAYMENT_METHODS.GOOGLEPAY;
          if (isAdyenSessionExpiredError(data)) {
            setSessionExpired(true);
            setError("Too many failed payment attempts. Your session has expired. Please return to billing details to try again.");
            return;
          }
          if (isUserCancellationLike(data) ||
              (isAmbiguousShopperAbort(data) && (!isVoucherPayment || !isVoucherPaymentServiceError(data)))) {
            softResetAfterUserCancel();
          } else {
            const errMsg = handleFailedAttempt(
              data.sessionResult,
              data.resultCode || data.errorCode,
              ctxRef.current.activeMethod || expressFallback
            );
            setError(errMsg);
            if (errMsg.toLowerCase().includes("too many")) setSessionExpired(true);
            if (!isVoucherPayment && component?.handleError) component.handleError(data);
          }
        },
        onError: (err) => checkoutErrorHandlerRef.current(err),
      });

      checkoutRef.current = checkout;
      const pmRes = checkout.paymentMethodsResponse || null;
      setPmResponse(pmRes);

      /**
       * Apple Pay requires a secure document (`https:`). Plain `http://localhost` throws
       * InvalidAccessError when starting a session — only enable Apple Pay on HTTPS (e.g. https://localhost with a dev cert).
       * Google Pay TEST often works on http localhost; keep that for dev.
       */
      const pmList = pmRes?.paymentMethods ?? [];
      const hasApple = pmList.some((pm) => pm.type === "applepay");
      const hasGoogle = pmList.some((pm) => pm.type === "googlepay" || pm.type === "paywithgoogle");

      const isHttps =
        typeof window !== "undefined" && window.location.protocol === "https:";
      const googleOkOnHttpLocal =
        typeof window !== "undefined" &&
        window.location.protocol === "http:" &&
        (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

      setSupportsApplePay(isHttps && hasApple);
      setSupportsGooglePay((isHttps || googleOkOnHttpLocal) && hasGoogle);

      setLoading(false);
      setCheckoutReady(true);
    } catch (err) {
      logger.error("Session did not start: initiateCheckoutSession", getPaymentLogDetails(err));

      // 409 "already paid": the order cannot be paid twice (e.g. the shopper
      // navigated back to the payment page after completing payment) — there
      // is nothing to retry here, so send them to the homepage.
      if (
        err.response?.status === 409 &&
        /already paid/i.test(err.response?.data?.message || "")
      ) {
        navigate("/", { replace: true });
        return;
      }

      if (isVoucherPayment) {
        handleVoucherPaymentEvent(err, "setup");
      } else if (isAdyenSessionExpiredError(err)) {
        setSessionExpired(true);
        setError("Your payment session has expired. Please start a new session or contact us if this keeps happening.");
      } else {
        setError(isVoucherPayment
          ? "We couldn’t load your payment options. Please try again or contact our team if the problem continues."
          : err.response?.data?.message || err.message || "Failed to start payment session. Please try again.");
      }
      setLoading(false);
      setCheckoutReady(false);
      setSupportsApplePay(false);
      setSupportsGooglePay(false);
    }
  };

  /** Express checkout: Apple Pay & Google Pay — mounted once per session when supported. */
  useLayoutEffect(() => {
    if (!checkoutReady || loading || !checkoutRef.current) return;

    const mountExpress = () => {
      if (supportsApplePay && applePayContainerRef.current) {
        try {
          unmountComponentEntry(componentsRef.current[PAYMENT_METHODS.APPLEPAY]);
          delete componentsRef.current[PAYMENT_METHODS.APPLEPAY];
          applePayContainerRef.current.innerHTML = "";
          const cmp = createApplePayComponent(checkoutRef.current, { amount, currency });
          cmp.mount(applePayContainerRef.current);
          componentsRef.current[PAYMENT_METHODS.APPLEPAY] = cmp;
        } catch (err) {
          logger.error("ApplePay initialization failed:", getPaymentLogDetails(err));
          setError("Apple Pay is not available. Please try another method.");
        }
      } else {
        unmountComponentEntry(componentsRef.current[PAYMENT_METHODS.APPLEPAY]);
        delete componentsRef.current[PAYMENT_METHODS.APPLEPAY];
        if (applePayContainerRef.current) applePayContainerRef.current.innerHTML = "";
      }

      if (supportsGooglePay && googlePayContainerRef.current) {
        try {
          unmountComponentEntry(componentsRef.current[PAYMENT_METHODS.GOOGLEPAY]);
          delete componentsRef.current[PAYMENT_METHODS.GOOGLEPAY];
          googlePayContainerRef.current.innerHTML = "";
          const cmp = createGooglePayComponent(checkoutRef.current);
          cmp.mount(googlePayContainerRef.current);
          componentsRef.current[PAYMENT_METHODS.GOOGLEPAY] = cmp;
        } catch (err) {
          logger.error("GooglePay initialization failed:", getPaymentLogDetails(err));
          setError("Google Pay is not available. Please try another method.");
        }
      } else {
        unmountComponentEntry(componentsRef.current[PAYMENT_METHODS.GOOGLEPAY]);
        delete componentsRef.current[PAYMENT_METHODS.GOOGLEPAY];
        if (googlePayContainerRef.current) googlePayContainerRef.current.innerHTML = "";
      }
    };

    requestAnimationFrame(mountExpress);
  }, [checkoutReady, loading, supportsApplePay, supportsGooglePay, amount, currency, pmRemountTick, setError]);

  /** Regular accordion methods — single mount target. */
  useLayoutEffect(() => {
    if (!checkoutReady || loading || !checkoutRef.current || !selectedRowKey || !activeMethod) {
      return undefined;
    }

    if (activeMethod === PAYMENT_METHODS.APPLEPAY || activeMethod === PAYMENT_METHODS.GOOGLEPAY) {
      return undefined;
    }

    const row = paymentRows.find((r) => r.rowKey === selectedRowKey);
    if (!row || row.adyenType === "applepay" || row.adyenType === "paywithgoogle") {
      return undefined;
    }

    let cancelled = false;
    setMountingPm(true);

    const runMount = () => {
      if (cancelled || !checkoutRef.current || !paymentMountRef.current) {
        setMountingPm(false);
        return;
      }

      unmountRegularComponents();

      const onCardChange = (state) => {
        if (state.isValid && state.data?.paymentMethod?.encryptedSecurityCode) {
          encryptedCvcRef.current = state.data.paymentMethod.encryptedSecurityCode;
        } else {
          encryptedCvcRef.current = "";
        }
      };

      const onCardError = (err) => {
        encryptedCvcRef.current = "";
        if (isVoucherPayment) checkoutErrorHandlerRef.current(err);
      };

      const ctx = {
        amount,
        currency,
        baseUrl,
        selectedStoredCard,
        onCardChange,
        onCardError,
      };

      const component = createPaymentComponent(checkoutRef.current, row, ctx);
      if (cancelled || !component) {
        setMountingPm(false);
        if (!component) setError(`Payment method ${row.label} could not be loaded.`);
        return;
      }

      try {
        component.mount(paymentMountRef.current);
        const refKey = row.internalKey;
        componentsRef.current[refKey] = component;
        setMountingPm(false);
      } catch (e) {
        logger.error("Payment component error", getPaymentLogDetails(e));
        setMountingPm(false);
        setError(isVoucherPayment
          ? `${row.label} could not be loaded. Please try another payment method.`
          : `Payment method ${row.label} not loaded: ${e.message}`);
      }
    };

    requestAnimationFrame(runMount);

    return () => {
      cancelled = true;
      setMountingPm(false);
    };
  }, [
    checkoutReady,
    loading,
    selectedRowKey,
    activeMethod,
    paymentRows,
    cardOption,
    selectedStoredCard,
    amount,
    currency,
    baseUrl,
    unmountRegularComponents,
    pmRemountTick,
    isVoucherPayment,
    setError,
  ]);

  const handleRetry = async () => {
    setActiveMethod(null);
    setSelectedRowKey(null);
    setSelectedStoredCard(null);
    encryptedCvcRef.current = "";
    setAuthenticationBlocked(false);
    setError(null);
    setSessionExpired(false);
    setComplete(false);
    setCardStored(false);
    setLoading(false);
    setReference(null);
    setCardOption("new");

    Object.entries(componentsRef.current).forEach(([, component]) => unmountComponentEntry(component));
    componentsRef.current = {};

    checkoutRef.current = null;
    setPmResponse(null);
    setCheckoutReady(false);

    await initiateCheckoutSession();
  };

  const handleSwitchMethod = async () => {
    setAuthenticationBlocked(false);
    setSessionExpired(false);
    setError(null);
    setLoading(false);
    setComplete(false);
    setCardStored(false);
    encryptedCvcRef.current = "";
    setActiveMethod(null);
    setSelectedRowKey(null);
    setSelectedStoredCard(null);
    setCardOption("new");

    Object.entries(componentsRef.current).forEach(([, component]) => unmountComponentEntry(component));
    componentsRef.current = {};

    if (paymentMountRef.current) paymentMountRef.current.innerHTML = "";

    if (checkoutRef.current && (supportsApplePay || supportsGooglePay)) {
      delete componentsRef.current[PAYMENT_METHODS.APPLEPAY];
      delete componentsRef.current[PAYMENT_METHODS.GOOGLEPAY];
      await new Promise((r) => requestAnimationFrame(r));
      try {
        if (supportsApplePay && applePayContainerRef.current) {
          applePayContainerRef.current.innerHTML = "";
          const cmp = createApplePayComponent(checkoutRef.current, { amount, currency });
          cmp.mount(applePayContainerRef.current);
          componentsRef.current[PAYMENT_METHODS.APPLEPAY] = cmp;
        }
        if (supportsGooglePay && googlePayContainerRef.current) {
          googlePayContainerRef.current.innerHTML = "";
          const cmp = createGooglePayComponent(checkoutRef.current);
          cmp.mount(googlePayContainerRef.current);
          componentsRef.current[PAYMENT_METHODS.GOOGLEPAY] = cmp;
        }
      } catch (e) {
        logger.error("Payment component error", getPaymentLogDetails(e));
      }
    }
  };

  const handleSelectRow = (row) => {
    if (scrollAnchorRef.current) {
      scrollAnchorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    if (selectedRowKey === row.rowKey) {
      setSelectedRowKey(null);
      setActiveMethod(null);
      encryptedCvcRef.current = "";
      unmountRegularComponents();
      return;
    }

    setSelectedRowKey(row.rowKey);
    setActiveMethod(row.internalKey);
    setSelectedStoredCard(null);
    encryptedCvcRef.current = "";
    setCardOption("new");
  };

  // Lockouts disabled — every method stays selectable; Adyen decides when the
  // session is exhausted. Children keep their lock props for easy re-enabling.
  const isGooglePayLocked = false;
  const isApplePayLocked = false;
  const isMethodLocked = () => false;
  const lockSecondsRemaining = () => 0;

  const renderExpanded = () => (
    <PaymentMethodRenderer mounting={mountingPm} mountRef={paymentMountRef} />
  );

  return (
    <div className="payment-method" ref={scrollAnchorRef}>
      <PaymentStatus
        useModal={isVoucherPayment && paymentError?.source === "api"}
        errorTitle={paymentError?.title || (isVoucherPayment ? "Payment options are unavailable" : undefined)}
        allowRetry={paymentError?.allowRetry !== false}
        retryLabel={paymentError?.retryLabel}
        orderReference={isVoucherPayment ? voucherOrderData?.order?.order_reference || paymentUUID : undefined}
        authenticationBlocked={authenticationBlocked}
        canSwitchMethod={!isVoucherPayment || checkoutReady}
        complete={complete}
        reference={reference}
        cardStored={cardStored}
        loading={loading}
        error={error}
        sessionExpired={sessionExpired}
        activeMethod={activeMethod}
        attemptKey={isVoucherPayment ? resolveAttemptKeyForCallbacks() : activeMethod}
        failedAttemptsByMethod={NO_LOCKS}
        lockExpiries={NO_LOCKS}
        initiateCheckoutSession={handleRetry}
        handleNewSession={() => {
          // An expired Adyen session leaves the voucher order bound to a dead
          // payment attempt — drop the order reference so billing details
          // creates a fresh order (selection and contact details are kept).
          if (isVoucherPayment && sessionExpired) setVoucherOrderData(null);
          navigate('/billing-details');
        }}
        handleSwitchMethod={handleSwitchMethod}
        selectedStoredCard={selectedStoredCard}
      />
      {!complete && !loading && !error && (
        <>
          <ExpressCheckout
            supportsApplePay={supportsApplePay}
            supportsGooglePay={supportsGooglePay}
            isApplePayLocked={isApplePayLocked}
            isGooglePayLocked={isGooglePayLocked}
            appleLockSeconds={lockSecondsRemaining(PAYMENT_METHODS.APPLEPAY)}
            googleLockSeconds={lockSecondsRemaining(PAYMENT_METHODS.GOOGLEPAY)}
            applePayRef={applePayContainerRef}
            googlePayRef={googlePayContainerRef}
          />

          {(supportsApplePay || supportsGooglePay) && (
            <h2 className={`${paymentStyles.sectionTitle} titleSub`}>More payment options</h2>
          )}

          {!supportsApplePay && !supportsGooglePay ? (
            <h2 className={`${paymentStyles.sectionTitle} titleSub`}>Payment methods</h2>
          ) : null}

          <PaymentAccordion
            rows={paymentRows}
            selectedRowKey={selectedRowKey}
            onSelectRow={handleSelectRow}
            isMethodLocked={isMethodLocked}
            lockSecondsRemaining={lockSecondsRemaining}
            renderExpanded={renderExpanded}
          />
        </>
      )}
    </div>
  );
};

// Wrap with Error Boundary to catch payment errors
const PaymentMethodWithErrorBoundary = (props) => (
  <ErrorBoundary componentName="Payment Method" fallbackMessage="Payment processing encountered an error. Please try again or use a different payment method.">
    <PaymentMethod {...props} />
  </ErrorBoundary>
);

export default PaymentMethodWithErrorBoundary;
